import { useState } from 'react';
import { useParams, Link } from '@tanstack/react-router';
import {
  ArrowLeft,
  Loader2,
  Plus,
  Upload,
  Eye,
  Trash2,
  Pencil,
  Package,
} from 'lucide-react';
import { Card, CardContent, EmptyState, ErrorState, useToast, useConfirm } from '@mustauto/ui/web';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableHeader,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from '@mustauto/ui/web';
import { useCampaignDetail } from '@/hooks/useCampaignDetail';
import {
  useCampaignProducts,
  useDeleteProduct,
} from '@/hooks/useCampaignCatalog';
import { formatReais, formatNumber } from '@/lib/utils';
import { ProductDrawer } from '@/components/ProductDrawer';
import { CsvImportModal } from '@/components/CsvImportModal';
import { PreviewModal } from '@/components/PreviewModal';
import { Breadcrumbs } from '@mustauto/ui/web';

type SortKey = 'clicks' | 'price' | 'delivery';

export function CampaignCatalogPage() {
  const { campaignId } = useParams({ from: '/app/campaigns/$campaignId/catalog' });
  const {
    data: campaignData,
    isLoading: campaignLoading,
    isError: campaignError,
    refetch: refetchCampaign,
  } = useCampaignDetail(campaignId);
  const {
    data: products,
    isLoading: productsLoading,
    isError: productsError,
    error: productsErrorObj,
    refetch: refetchProducts,
  } = useCampaignProducts(campaignId);
  const deleteMutation = useDeleteProduct(campaignId);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingProductId, setEditingProductId] = useState<string | null>(null);
  const [csvModalOpen, setCsvModalOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [sortBy, setSortBy] = useState<SortKey>('clicks');
  const { toast } = useToast();
  const confirm = useConfirm();

  async function handleDeleteProduct(productId: string, productName: string) {
    const ok = await confirm({
      title: 'Excluir produto?',
      description: `O produto "${productName}" será removido do catálogo. Esta ação não pode ser desfeita.`,
      confirmLabel: 'Excluir',
      dangerous: true,
    });
    if (!ok) return;
    deleteMutation.mutate(productId, {
      onSuccess: () => toast({ type: 'success', title: 'Produto excluído' }),
      onError: () =>
        toast({
          type: 'error',
          title: 'Não foi possível excluir o produto. Tente novamente.',
        }),
    });
  }

  const isLoading = campaignLoading || productsLoading;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const sortedProducts = [...(products ?? [])].sort((a, b) => {
    switch (sortBy) {
      case 'clicks': return b.clicks - a.clicks;
      case 'price': return Number(a.price) - Number(b.price);
      case 'delivery': return (a.deliveryDaysMin ?? 999) - (b.deliveryDaysMin ?? 999);
      default: return 0;
    }
  });

  const campaignName = campaignData?.campaign.name ?? '';
  const category = campaignData?.campaign.category ?? '';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <Breadcrumbs
          items={[
            { label: 'Dashboard', href: '/dashboard' },
            { label: 'Campanhas', href: '/campaigns' },
            { label: campaignName, href: `/campaigns/${campaignId}` },
            { label: 'Catálogo' },
          ]}
        />
        <div className="mt-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold">Catálogo de produtos</h1>
            <Badge variant="outline">{category}</Badge>
            <span className="text-sm text-muted-foreground">
              {products?.length ?? 0} produtos
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPreviewOpen(true)}
              className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-2 text-sm font-medium transition-colors hover:bg-muted"
            >
              <Eye size={14} />
              Ver como o cliente vê
            </button>
            <button
              onClick={() => setCsvModalOpen(true)}
              className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-2 text-sm font-medium transition-colors hover:bg-muted"
            >
              <Upload size={14} />
              Importar CSV
            </button>
            <button
              onClick={() => { setEditingProductId(null); setDrawerOpen(true); }}
              className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90"
            >
              <Plus size={14} />
              Adicionar produto
            </button>
          </div>
        </div>
      </div>

      {/* Sort controls */}
      <div className="flex items-center gap-2 text-sm">
        <span className="text-muted-foreground">Ordenar por:</span>
        {([
          ['clicks', 'Cliques'],
          ['price', 'Preço'],
          ['delivery', 'Prazo'],
        ] as const).map(([key, label]) => (
          <button
            key={key}
            onClick={() => setSortBy(key)}
            className={`rounded-md px-3 py-1 transition-colors ${
              sortBy === key
                ? 'bg-primary text-primary-foreground font-medium'
                : 'bg-muted hover:bg-border'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Products table / empty state */}
      {(productsError || campaignError) && sortedProducts.length === 0 ? (
        /* `[...(products ?? [])]` faz a busca falhada chegar aqui como catalogo
           vazio, e a tela dizia "nenhum produto cadastrado" numa campanha que
           tem produtos — com o aviso de que a campanha nao apareceria para o
           cliente sem eles. */
        <Card>
          <CardContent className="p-0">
            <ErrorState
              title="Não foi possível carregar os produtos da campanha"
              error={productsErrorObj}
              /* O ramo dispara para qualquer uma das duas buscas, entao o retry
                 precisa cobrir as duas: so refazer a de produtos deixava o
                 botao sem efeito quando quem falhou foi a da campanha. */
              onRetry={() => {
                void refetchProducts();
                void refetchCampaign();
              }}
            />
          </CardContent>
        </Card>
      ) : sortedProducts.length === 0 ? (
        <Card>
          <CardContent className="p-0">
            {/* O cartao "Dica" que ficava aqui explicava por escrito que da para
                importar CSV e mandava procurar o botao no topo. Virou a acao
                secundaria: o vazio agora oferece o atalho em vez de descreve-lo. */}
            <EmptyState
              icon={<Package size={30} />}
              title="Nenhum produto cadastrado"
              description={'Sem produto, a campanha não aparece na tela "Onde comprar" do cliente. Você também pode importar vários de uma vez por CSV.'}
              action={{
                label: 'Adicionar primeiro produto',
                onClick: () => { setEditingProductId(null); setDrawerOpen(true); },
              }}
              secondaryAction={{ label: 'Importar CSV', onClick: () => setCsvModalOpen(true) }}
            />
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">Foto</TableHead>
                  <TableHead>Nome</TableHead>
                  <TableHead>SKU</TableHead>
                  <TableHead className="text-right">Preço</TableHead>
                  <TableHead>Prazo</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Cliques</TableHead>
                  <TableHead className="w-20">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedProducts.map((product) => (
                  <TableRow key={product.id}>
                    <TableCell>
                      {product.photoUrl ? (
                        <img
                          src={product.photoUrl}
                          alt={product.name}
                          className="h-10 w-10 rounded-md object-cover"
                        />
                      ) : (
                        <div className="flex h-10 w-10 items-center justify-center rounded-md bg-muted text-xs text-muted-foreground">
                          —
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="font-medium">{product.name}</TableCell>
                    <TableCell className="text-muted-foreground">{product.sku ?? '—'}</TableCell>
                    <TableCell className="text-right font-medium">
                      {formatReais(Number(product.price))}
                    </TableCell>
                    <TableCell>
                      {product.deliveryDaysMin != null
                        ? `${product.deliveryDaysMin}${product.deliveryDaysMax ? `-${product.deliveryDaysMax}` : ''} dias`
                        : '—'}
                    </TableCell>
                    <TableCell>
                      {product.outOfStock ? (
                        <Badge variant="secondary">Sem estoque</Badge>
                      ) : product.active ? (
                        <Badge variant="success">Ativo</Badge>
                      ) : (
                        <Badge variant="secondary">Inativo</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">{formatNumber(product.clicks)}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => { setEditingProductId(product.id); setDrawerOpen(true); }}
                          className="rounded p-1 hover:bg-muted"
                          title="Editar"
                        >
                          <Pencil size={14} />
                        </button>
                        <button
                          onClick={() => handleDeleteProduct(product.id, product.name)}
                          className="rounded p-1 text-destructive hover:bg-destructive/10"
                          title="Excluir"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Drawers / Modals */}
      {drawerOpen && (
        <ProductDrawer
          campaignId={campaignId}
          productId={editingProductId}
          onClose={() => setDrawerOpen(false)}
        />
      )}
      {csvModalOpen && (
        <CsvImportModal
          campaignId={campaignId}
          onClose={() => setCsvModalOpen(false)}
        />
      )}
      {previewOpen && (
        <PreviewModal
          campaignId={campaignId}
          onClose={() => setPreviewOpen(false)}
        />
      )}
    </div>
  );
}
